/**
 * Achievements Scene: displays the player's unlocked achievements.
 *
 * Accessible from the TitleScene menu. Shows 10 achievements with
 * locked/unlocked status, names, and descriptions. Persistent data
 * is read from localStorage via achievement-manager.js.
 */

import Phaser from 'phaser';
import { SCENE_ACHIEVEMENTS, SCENE_TITLE } from '../utils/constants.js';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  COLOR_WHITE,
  BORDER_MARGIN,
  AUDIO_MENU_CLICK,
  AUDIO_ESC_PRESSED,
  COLOR_ACHIEVEMENT_GOLD,
  COLOR_ACHIEVEMENT_LOCKED,
} from '../config/game-config.js';
import { getSfxVol, applyMasterMute, refreshConfig } from '../utils/audio-config.js';
import {
  ACHIEVEMENT_DEFINITIONS,
  getUnlockedAchievements,
  getUnlockedCount,
  getTotalCount,
} from '../utils/achievement-manager.js';

// --- Text styles (module-level) ---

const HEADER_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '22px',
  color: '#ffffff',
  letterSpacing: 3,
};

const COUNTER_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#aaaaaa',
};

const NAME_UNLOCKED_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '14px',
  color: '#ffd700',
};

const NAME_LOCKED_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '14px',
  color: '#999999',
};

const DESC_UNLOCKED_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#cccccc',
};

const DESC_LOCKED_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#777777',
};

const HINT_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#666666',
};

// Layout
const CX = GAME_WIDTH / 2;
const CONTENT_LEFT = BORDER_MARGIN + 40;
const LIST_START_Y = 120;
const ROW_HEIGHT = 42;

export default class AchievementsScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENE_ACHIEVEMENTS });
  }

  create() {
    this.cameras.main.setBackgroundColor('#000000');
    this.cameras.main.fadeIn(400, 0, 0, 0);

    refreshConfig();
    applyMasterMute(this.sound);

    this._transitioning = false;

    this._drawBorder();
    this._drawContent();
    this._setupInput();
  }

  // ---------------------------------------------------------------
  // Content
  // ---------------------------------------------------------------

  /** @private */
  _drawContent() {
    const unlocked = getUnlockedAchievements();
    const unlockedCount = getUnlockedCount();
    const totalCount = getTotalCount();

    // Header
    this.add.text(CX, 55, 'ACHIEVEMENTS', HEADER_STYLE).setOrigin(0.5);

    // Counter
    this.add.text(CX, 82, `${unlockedCount} / ${totalCount} unlocked`, COUNTER_STYLE)
      .setOrigin(0.5);

    // Separator line
    const sep = this.add.graphics();
    sep.lineStyle(1, COLOR_WHITE, 0.15);
    sep.lineBetween(CONTENT_LEFT, 100, GAME_WIDTH - CONTENT_LEFT, 100);

    // Achievement rows
    ACHIEVEMENT_DEFINITIONS.forEach((def, i) => {
      const y = LIST_START_Y + i * ROW_HEIGHT;
      const isUnlocked = unlocked.has(def.id);

      // Status icon (simple text marker)
      const iconColor = isUnlocked ? '#ffd700' : '#333333';
      const iconChar = isUnlocked ? '\u2605' : '\u2606'; // ★ or ☆
      this.add.text(CONTENT_LEFT, y, iconChar, {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '16px',
        color: iconColor,
      });

      // Name
      const nameStyle = isUnlocked ? NAME_UNLOCKED_STYLE : NAME_LOCKED_STYLE;
      this.add.text(CONTENT_LEFT + 24, y, def.name, nameStyle);

      // Description
      const descStyle = isUnlocked ? DESC_UNLOCKED_STYLE : DESC_LOCKED_STYLE;
      this.add.text(CONTENT_LEFT + 24, y + 18, def.description, descStyle);
    });

    // Hint
    const hint = this.add.text(CX, GAME_HEIGHT - BORDER_MARGIN - 20,
      'ESC to return', HINT_STYLE).setOrigin(0.5);
    this.tweens.add({
      targets: hint,
      alpha: 0.3,
      duration: 700,
      yoyo: true,
      repeat: -1,
    });
  }

  // ---------------------------------------------------------------
  // Input
  // ---------------------------------------------------------------

  /** @private */
  _setupInput() {
    this.input.keyboard.on('keydown-ESC', () => this._returnToTitle());
    this.input.keyboard.on('keydown-ENTER', () => this._returnToTitle());
    this.input.keyboard.on('keydown-SPACE', () => this._returnToTitle());
  }

  // ---------------------------------------------------------------
  // Transitions
  // ---------------------------------------------------------------

  /** @private */
  _returnToTitle() {
    if (this._transitioning) return;
    this._transitioning = true;

    try {
      this.sound.play(AUDIO_ESC_PRESSED, { volume: getSfxVol(0.4) });
    } catch (_) { /* audio fallback */ }

    this.cameras.main.fadeOut(400, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(SCENE_TITLE);
    });
  }

  // ---------------------------------------------------------------
  // Border
  // ---------------------------------------------------------------

  /** @private */
  _drawBorder() {
    const g = this.add.graphics();
    g.lineStyle(1, COLOR_WHITE, 0.2);
    g.strokeRect(BORDER_MARGIN, BORDER_MARGIN,
      GAME_WIDTH - BORDER_MARGIN * 2, GAME_HEIGHT - BORDER_MARGIN * 2);

    const cornerSize = 10;
    const m = BORDER_MARGIN + 5;
    g.lineStyle(1, COLOR_WHITE, 0.4);

    // Top-left
    g.lineBetween(m, m, m + cornerSize, m);
    g.lineBetween(m, m, m, m + cornerSize);
    // Top-right
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m - cornerSize, m);
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m, m + cornerSize);
    // Bottom-left
    g.lineBetween(m, GAME_HEIGHT - m, m + cornerSize, GAME_HEIGHT - m);
    g.lineBetween(m, GAME_HEIGHT - m, m, GAME_HEIGHT - m - cornerSize);
    // Bottom-right
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m - cornerSize, GAME_HEIGHT - m);
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m, GAME_HEIGHT - m - cornerSize);
  }
}
